import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { formatDate } from "../shared/format";
import type { VersionView } from "../shared/types";
import { useInvalidateSource, useSourceVersions } from "./use-source-detail";

function VersionRow({ version, sourceId, canEdit }: { version: VersionView; sourceId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateSource(sourceId);
  const k = "deployKnowledge.sourceDetail.versions";
  const restore = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/sources/{source_id}/versions/{version}/restore", { params: { path: { source_id: sourceId, version: version.version } } })),
    onSuccess: async () => {
      toast.info(t(`${k}.restoring`, { version: version.version }));
      await invalidate();
    },
    onError: (error) => toast.error(message(error)),
  });
  const meta = [formatDate(version.created_at, i18n.language), t(`${k}.chunks`, { count: version.chunk_count }), version.note].filter(Boolean).join(" · ");
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <Badge tone={version.active ? "success" : "neutral"}>v{version.version}</Badge>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{version.active ? t(`${k}.current`) : version.title}</div>
        <div className="truncate text-xs text-fg-subtle">{meta}</div>
      </div>
      {version.active ? (
        <span className="text-[11px] font-semibold text-fg-subtle">{t(`${k}.live`)}</span>
      ) : canEdit ? (
        <Button variant="ghost" size="sm" aria-label={t(`${k}.restoreLabel`, { version: version.version })} loading={restore.isPending} onClick={() => { restore.mutate(); }}>
          <Icon name="rotate-ccw" />
          {t(`${k}.restore`)}
        </Button>
      ) : null}
    </li>
  );
}

/** `GET /v1/sources/{id}/versions` + restore (old version stays live until the restored one is ready). */
export function VersionsCard({ sourceId, canEdit }: { sourceId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const versions = useSourceVersions(sourceId);
  const k = "deployKnowledge.sourceDetail.versions";
  return (
    <section className="card" aria-labelledby="source-versions-title">
      <div className="card-header">
        <h2 id="source-versions-title" className="card-title flex items-center gap-2">
          <Icon name="history" className="size-4 text-fg-subtle" />
          {t(`${k}.title`)}
        </h2>
      </div>
      {versions.isPending ? (
        <div className="space-y-3 px-5 py-4" aria-hidden="true">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : versions.isError ? (
        <ErrorState headingLevel={3} title={t("deployKnowledge.loadError")} onRetry={() => void versions.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <ul className="divide-y divide-line">
          {[...versions.data]
            .sort((a, b) => b.version - a.version)
            .map((version) => (
              <VersionRow key={version.version} version={version} sourceId={sourceId} canEdit={canEdit} />
            ))}
          {versions.data.length <= 1 ? <li className="px-5 py-3 text-xs text-fg-subtle">{t(`${k}.empty`)}</li> : null}
        </ul>
      )}
    </section>
  );
}
